import { z } from 'zod'

const driverDetailSchema = z.object({
  licenceNumber: z
    .string()
    .max(50, 'License number must be at most 50 characters')
    .min(1, 'License number is required'),
  licenceExpiry: z
    .union([z.string(), z.date(), z.null()])
    .transform((val) => {
      if (val === undefined || val === null) return undefined
      if (val instanceof Date) return val
      if (typeof val === 'string' && val.trim() !== '') {
        const d = new Date(val)
        return isNaN(d.getTime()) ? undefined : d
      }
      return undefined
    })
    .refine((val) => {
      if (!val || !(val instanceof Date)) return true
      const now = new Date()
      now.setHours(0, 0, 0, 0)
      return val >= now
    }, 'License expiry date cannot be in the past'),
})

const userTypeSchema = z.object({
  id: z.number().min(1, 'User Type is required'),
  name: z.string().optional(),
})

export const workforceSchema = z
  .object({
    id: z.number().optional(),
    firstName: z
      .string()
      .max(150, 'First Name must be at most 150 characters')
      .min(1, 'First Name is required'),
    lastName: z
      .string()
      .max(150, 'Last Name must be at most 150 characters')
      .min(1, 'Last Name is required'),
    email: z
      .string()
      .min(1, 'Email is required')
      .max(250, 'Email must be at most 250 characters')
      .email('Invalid email address')
      .refine((email) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        return emailRegex.test(email)
      }, 'Invalid email address format'),
    phone: z
      .string()
      .min(1, 'Phone is required')
      .nullable()
      .refine((phone) => {
        const phoneRegex = /^[+]?[\d\s\-()]+$/
        return phoneRegex.test(phone || '')
      }, 'Phone number contains invalid characters or exceeds 15 characters'),
    disabled: z.boolean().optional(),
    userTypeId: z.number().optional(),
    userType: userTypeSchema.optional(),
    tenantId: z.number(),
    driverDetail: z.union([driverDetailSchema, z.undefined()]).optional(),
    documents: z.array(z.any()).optional(),
    validFrom: z.union([z.date(), z.string()]).nullable().optional(),
    validTo: z.union([z.date(), z.string()]).nullable().optional(),
  })
  .refine(
    (data) => {
      if (data.userType?.name === 'Driver') {
        return (
          data.driverDetail &&
          data.driverDetail.licenceNumber &&
          data.driverDetail.licenceExpiry instanceof Date
        )
      }
      return true
    },
    {
      message: 'Driver details are required when user type is Driver',
      path: ['driverDetail'],
    }
  )
  .refine(
    (data) => {
      if (data.userType?.name === 'Driver' && data.driverDetail) {
        return data.driverDetail.licenceExpiry instanceof Date
      }
      return true
    },
    {
      message: 'License expiry date is required for drivers',
      path: ['driverDetail', 'licenceExpiry'],
    }
  )

export type WorkforceFormData = z.infer<typeof workforceSchema>
